/**
 * Email templates — docs/11 §Data → Email Templates.
 *
 * The default wording lives here, in code, and a company may override any of
 * it. Each template declares the placeholders it may use, so the editor can
 * list them and the renderer can refuse one that was invented.
 */

export interface EmailTemplateDef {
  readonly key: string
  readonly label: string
  /** When this is sent, in the words of whoever is editing it. */
  readonly when: string
  readonly placeholders: readonly string[]
  readonly subject: string
  readonly body: string
  /**
   * Sent to somebody outside the company about a document. Only these offer
   * the copy options, because only these have a project team to copy and an
   * outside recipient to keep a copy away from (docs/11 §Email Info).
   */
  readonly outward?: boolean
}

/**
 * Who else gets a copy — `Cc Project Team`, `Bcc Project Team`,
 * `Add sender in Bcc` (docs/11 §Email Info).
 *
 * Cc and Bcc of the same team are the same people and a different disclosure:
 * an order copied openly tells the supplier who is watching it, which is
 * sometimes the point and sometimes exactly what a company does not want a
 * supplier to know. So both are offered and they are mutually exclusive.
 *
 * `senderInBcc` is the one people actually miss: whoever pressed send has no
 * copy in their own sent items, because the message left from the system's
 * address, not theirs.
 */
export interface EmailCopyOptions {
  readonly ccProjectTeam: boolean
  readonly bccProjectTeam: boolean
  readonly senderInBcc: boolean
}

/** Copying the team openly is what the system did before this was settable. */
export const DEFAULT_COPY_OPTIONS: EmailCopyOptions = {
  ccProjectTeam: true,
  bccProjectTeam: false,
  senderInBcc: false,
}

const def = <const T extends EmailTemplateDef>(t: T): T => t

/** Placeholders every template may use. */
const COMMON = ['companyName', 'documentNo', 'documentDate', 'total', 'partyName', 'projectName', 'senderName'] as const

export const EMAIL_TEMPLATES = [
  def({
    key: 'purchase_order_sent',
    outward: true,
    label: 'Purchase order to a supplier',
    when: 'When a purchase order is emailed to the supplier.',
    placeholders: [...COMMON, 'deliveryDate'],
    subject: 'Purchase order {{documentNo}} from {{companyName}}',
    body: `Dear {{partyName}},

Please find our purchase order {{documentNo}} dated {{documentDate}} for {{total}}.

{{#deliveryDate}}We need delivery by {{deliveryDate}}.{{/deliveryDate}}

Please acknowledge receipt of this order and confirm the delivery date.

Regards,
{{senderName}}
{{companyName}}`,
  }),
  def({
    key: 'subcontract_order_sent',
    outward: true,
    label: 'Subcontract order to a subcontractor',
    when: 'When a subcontract order is emailed to the subcontractor.',
    placeholders: [...COMMON],
    subject: 'Subcontract order {{documentNo}} from {{companyName}}',
    body: `Dear {{partyName}},

Please find our subcontract order {{documentNo}} dated {{documentDate}} for {{total}}, for work at {{projectName}}.

Please acknowledge this order and confirm your start date.

Regards,
{{senderName}}
{{companyName}}`,
  }),
  def({
    key: 'rfq_invitation',
    outward: true,
    label: 'Request for quote to a supplier',
    when: 'When suppliers are invited to quote against an RFQ.',
    placeholders: [...COMMON, 'link', 'closingDate'],
    subject: 'Request for quote {{documentNo}} from {{companyName}}',
    body: `Dear {{partyName}},

We invite your quotation against our request {{documentNo}} dated {{documentDate}}.

Please enter your rates here:
{{link}}

{{#closingDate}}The request closes on {{closingDate}}.{{/closingDate}}

The link is for you alone. Please do not forward it.

Regards,
{{senderName}}
{{companyName}}`,
  }),
  def({
    key: 'approval_request',
    label: 'Something is waiting for an approval',
    when: 'When a document is submitted and someone else has to approve it.',
    placeholders: [...COMMON, 'docTypeLabel', 'raisedBy'],
    subject: '{{docTypeLabel}} {{documentNo}} is waiting for your approval',
    body: `{{raisedBy}} has submitted {{docTypeLabel}} {{documentNo}} for {{total}}.

Project: {{projectName}}
Party: {{partyName}}

It is waiting for your approval.

{{companyName}}`,
  }),
  def({
    key: 'document_rejected',
    label: 'A document has been sent back',
    when: 'When an approver returns a document to whoever raised it.',
    placeholders: [...COMMON, 'docTypeLabel', 'reason', 'sentBackBy'],
    subject: '{{docTypeLabel}} {{documentNo}} has been sent back',
    body: `{{sentBackBy}} has sent {{docTypeLabel}} {{documentNo}} back for changes.

Reason: {{reason}}

It is a draft again, and keeps its number.

{{companyName}}`,
  }),
  def({
    key: 'workflow_alert',
    label: 'An approval route alert',
    when: 'When something happens to a document that its approval route says to tell people about.',
    placeholders: [...COMMON, 'docTypeLabel', 'actionLabel', 'actorName'],
    subject: '{{docTypeLabel}} {{documentNo}}: {{actionLabel}}',
    body: `{{actorName}} did "{{actionLabel}}" on {{docTypeLabel}} {{documentNo}}.

Project: {{projectName}}

You are told because the approval route for {{docTypeLabel}} names you, your role or your role on the project.

{{companyName}}`,
  }),
  def({
    key: 'data_export_ready',
    label: 'A data export is ready',
    when: 'When a whole-account export has been built and is ready to download.',
    placeholders: ['companyName', 'senderName', 'password', 'expiresOn', 'fileSize', 'requestedOn'],
    subject: 'Your data export from {{companyName}} is ready',
    body: `The export you asked for on {{requestedOn}} has been built and is waiting under
Settings → Export data.

The archive is password protected. The password is:

    {{password}}

Type it exactly, including the dashes. It is in this email and nowhere else —
nobody, including support, can read it back for you afterwards.

The download stops working on {{expiresOn}}. Ask for another export after that
if you still need one.

{{companyName}}`,
  }),
  def({
    key: 'data_export_failed',
    label: 'A data export could not be built',
    when: 'When a whole-account export fails while it is being assembled.',
    placeholders: ['companyName', 'senderName', 'reason', 'requestedOn'],
    subject: 'Your data export from {{companyName}} could not be built',
    body: `The export you asked for on {{requestedOn}} could not be built.

What went wrong: {{reason}}

Nothing was sent anywhere. You can ask for another one straight away — the
usual seven-day wait does not apply to an export that failed.

{{companyName}}`,
  }),
] as const satisfies readonly EmailTemplateDef[]

export type EmailTemplateKey = (typeof EMAIL_TEMPLATES)[number]['key']

const byKey = new Map<string, EmailTemplateDef>(EMAIL_TEMPLATES.map((t) => [t.key as string, t as EmailTemplateDef]))

export const getEmailTemplateDef = (key: string): EmailTemplateDef | undefined => byKey.get(key)

export interface EmailTemplateView {
  readonly key: string
  readonly label: string
  readonly when: string
  readonly placeholders: readonly string[]
  readonly subject: string
  readonly body: string
  /** False when the company is using the wording that ships with the system. */
  readonly customised: boolean
  readonly updatedBy: string | null
  /** Only meaningful, and only offered, on an outward template. */
  readonly outward: boolean
  readonly copies: EmailCopyOptions
}

export interface OutboxMessage {
  readonly id: string
  readonly templateKey: string
  readonly to: string
  readonly toName: string | null
  readonly subject: string
  readonly body: string
  readonly status: 'QUEUED' | 'SENT' | 'FAILED' | 'HELD' | 'TRAINING'
  readonly attempts: number
  readonly lastError: string | null
  readonly documentNo: string | null
  readonly route: string | null
  readonly documentId: string | null
  readonly createdAt: string
  readonly sentAt: string | null
  /** The printed document that went with it, by its file name. */
  readonly attachmentName?: string | null
}
